import { useState } from 'react';

interface PharmacyGalleryProps {
  images: string[];
  name: string;
}

const PharmacyGallery = ({ images, name }: PharmacyGalleryProps) => {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  return (
    <>
      <div className="flex flex-col gap-3">
        {/* Main image */}
        <div
          className="relative w-full rounded-2xl overflow-hidden cursor-pointer group"
          style={{ height: '380px', border: '1px solid rgba(0,245,255,0.15)' }}
          onClick={() => setLightbox(true)}
        >
          <img
            src={images[active]}
            alt={`${name} — фото ${active + 1}`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {/* Scanlines */}
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,245,255,0.015) 2px, rgba(0,245,255,0.015) 4px)' }}
          />
          {/* Corner accents */}
          {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((pos, i) => (
            <div key={i} className={`absolute ${pos} w-5 h-5 pointer-events-none`}>
              <div className="absolute inset-0" style={{
                borderTop: i < 2 ? '1px solid rgba(0,245,255,0.6)' : 'none',
                borderBottom: i >= 2 ? '1px solid rgba(0,245,255,0.6)' : 'none',
                borderLeft: i % 2 === 0 ? '1px solid rgba(0,245,255,0.6)' : 'none',
                borderRight: i % 2 === 1 ? '1px solid rgba(0,245,255,0.6)' : 'none',
              }} />
            </div>
          ))}
          {/* Zoom hint */}
          <div
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ background: 'rgba(5,5,8,0.85)', border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff', backdropFilter: 'blur(8px)' }}
          >
            <i className="ri-zoom-in-line" />
            Увеличить
          </div>
          {/* Counter */}
          <div
            className="absolute top-3 right-3 px-2.5 py-1 rounded-lg font-mono text-xs"
            style={{ background: 'rgba(5,5,8,0.8)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
          >
            {active + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnails */}
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="relative rounded-xl overflow-hidden cursor-pointer transition-all duration-200"
              style={{
                height: '72px',
                border: active === i ? '2px solid #ff2d78' : '1px solid rgba(255,255,255,0.08)',
                boxShadow: active === i ? '0 0 12px rgba(255,45,120,0.3)' : 'none',
              }}
            >
              <img src={img} alt={`Фото ${i + 1}`} className="w-full h-full object-cover object-top" />
              {active !== i && <div className="absolute inset-0 bg-black/40" />}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(5,5,8,0.95)', backdropFilter: 'blur(12px)' }}
          onClick={() => setLightbox(false)}
        >
          <div className="relative max-w-5xl w-full" onClick={e => e.stopPropagation()}>
            <img
              src={images[active]}
              alt={`${name} — фото ${active + 1}`}
              className="w-full rounded-2xl object-cover"
              style={{ maxHeight: '80vh' }}
            />
            {/* Nav arrows */}
            {active > 0 && (
              <button
                onClick={() => setActive(a => a - 1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all"
                style={{ background: 'rgba(5,5,8,0.8)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }}
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
            )}
            {active < images.length - 1 && (
              <button
                onClick={() => setActive(a => a + 1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all"
                style={{ background: 'rgba(5,5,8,0.8)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }}
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            )}
            {/* Close */}
            <button
              onClick={() => setLightbox(false)}
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all"
              style={{ background: 'rgba(5,5,8,0.8)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
            >
              <i className="ri-close-line" />
            </button>
            {/* Dots */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className="rounded-full cursor-pointer transition-all duration-200"
                  style={{
                    width: active === i ? '20px' : '6px',
                    height: '6px',
                    background: active === i ? '#ff2d78' : 'rgba(255,255,255,0.25)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PharmacyGallery;
